'use client'

import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useLoadingStore } from '@repo/store/useLoadingStore'
import { useNavigate } from 'react-router-dom'
import MUIStack from '@mui/material/Stack'
import MUIButton from '@mui/material/Button'
import MUITypography from '@mui/material/Typography'
import MUIInput from '@mui/material/TextField'
import MUIMenuItem from '@mui/material/MenuItem'
import { PasswordInput } from '@repo/ui/PasswordInput'
import { CheckIcon } from '@repo/ui/CheckIcon'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { AuthScreenTitle } from '@repo/ui/AuthScreenTitle'

// this page is loaded when the user clicks a password reset link in their email
// the link contains a token that is used to verify the user's identity. It is added to the URL as a query param called 'token'
// the user is then prompted to enter a new password, and this is sent along with the token to the server to complete the password reset
// if the user has multiple accounts with the same email address, they will be prompted to select the account they wish to reset the password for, and will need to reset the form

const RequestPasswordReset = ({ request_DI }) => {
  const { completePasswordReset } = useAuthenticationStore()
  const [userId, setUserId] = useState(null)
  const [password, setPassword] = useState('')
  const [passwordResetToken, setPasswordResetToken] = useState('')
  const [passwordIsValid, setPasswordIsValid] = useState(false)
  const [requireUsernameInput, setRequireUsernameInput] = useState(false)
  const [usernameOptions, setUsernameOptions] = useState([])
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { isLoading } = useLoadingStore()
  const navigate = useNavigate()

  useEffect(() => {
    const searchString = location.hash.substring(location.hash.indexOf('?'))
    const urlSearchParams = new URLSearchParams(searchString)
    urlSearchParams.forEach((value, key) => {
      if (key === 'token') {
        setPasswordResetToken(value)
      }
    })
  }, [])

  const handleSubmit = async (e) => {
    if (isLoading) return

    try {
      e.preventDefault()
      setErrorMessage('')
      const res = await completePasswordReset({
        userId,
        newPassword: password,
        passwordResetToken,
        makeApiCall: request_DI
      })
      setUsernameOptions([])
      setRequireUsernameInput(false)
      if (res) setFormSubmitted(true)
    } catch (err) {
      console.error(err)
      setFormSubmitted(false)

      const errorMessage = err?.response?.data?.errorMessage || ''

      // TODO improve this by looking for http status code 417
      if (errorMessage.includes(',')) {
        setUsernameOptions(errorMessage.split(','))
        setRequireUsernameInput(true)

        setErrorMessage(
          'Multiple accounts found with your email. Please select the username you wish to reset the password for.'
        )
      } else {
        setErrorMessage(err?.response?.data?.errorMessage || 'An error occurred. Please try again.')
      }
    }
  }

  return (
    <div style={{ marginTop: '-120px' }}>
      <AuthScreenTitle title="Set new password" description={formSubmitted ? null : 'Enter your new password below'} />

      {formSubmitted ? (
        <MUIStack direction="column" alignItems="flex-start" justifyContent="flex-start" gap={10} sx={{ mt: -12 }}>
          <MUIStack direction="row" alignItems="flex-start" justifyContent="flex-start" gap={4}>
            <CheckIcon sx={{ width: '34px', height: '34px' }} />
            <MUITypography variant="body2">Your password has been updated. Please continue to sign in</MUITypography>
          </MUIStack>

          <MUIButton
            variant="contained"
            data-testid="loginBtn"
            onClick={() => navigate(`/auth/login`)}
            color="primary"
            size="small"
            sx={{ height: '56px', width: '100%' }}
          >
            Log in now
          </MUIButton>
        </MUIStack>
      ) : (
        <form
          onSubmit={handleSubmit}
          style={{
            width: '100%',
            position: 'relative',
            cursor: isLoading ? 'wait' : 'default'
          }}
          data-testid="loginForm"
        >
          <MUIStack direction="column" alignItems="center" justifyContent="center" gap={4}>
            {requireUsernameInput ? (
              <MUIInput
                data-testid="userId"
                id="userId"
                name="userId"
                label="Username"
                className="usernameSelect"
                type="text"
                sx={{ width: '100%' }}
                onChange={(e) => setUserId(e.target.value)}
                required={requireUsernameInput}
                select
              >
                {usernameOptions.map((option) => (
                  <MUIMenuItem key={option} value={option} sx={{ width: '100%', minWidth: '240px !important' }}>
                    {option}
                  </MUIMenuItem>
                ))}
              </MUIInput>
            ) : null}

            <PasswordInput
              label="New password"
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              autoComplete={'new-password'}
              showPasswordStrengthIndicator
              onValidationChange={(val) => setPasswordIsValid(val)}
            />
            <MUIButton
              variant="contained"
              data-testid="loginBtn"
              type="submit"
              color="primary"
              size="small"
              sx={{ height: '56px', width: '100%' }}
              disabled={Boolean(isLoading) || !passwordIsValid}
            >
              {isLoading ? 'Loading...' : 'Reset password'}
            </MUIButton>

            <MUITypography
              variant="body1"
              align="center"
              data-testid="errorMessage"
              sx={{ width: '100%', color: 'error.main', position: 'absolute', bottom: '-90px' }}
            >
              {errorMessage}
            </MUITypography>
          </MUIStack>
        </form>
      )}
    </div>
  )
}

export default RequestPasswordReset

RequestPasswordReset.propTypes = {
  request_DI: PropTypes.func
}
